/**
 * Calendar tab for the organization profile page.
 *
 * Shows the saved calendar for a selected academic year, with an edit
 * button (org admins + super admins only, gated by the
 * calendar.editingEnabled feature flag).
 *
 * If no calendar exists yet for the selected year, the tab shows an
 * empty state. Org admins see a "Create calendar" button that opens the
 * same edit modal with a blank form.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Select } from 'flowbite-react';
import { HiPencil, HiPlus, HiCalendar } from 'react-icons/hi';

import { calendarApi, getUserErrorMessage } from '../../api';
import type { CalendarResponse, CalendarUpsert } from '../../api/calendar';
import { descriptorsApi, type Descriptor } from '../../api/descriptors';
import { useAppSettings, APP_SETTING_KEYS } from '../../contexts/AppSettingsContext';
import { EditCalendarModal } from '../calendar/EditCalendarModal';
import {
  compareYearCodesDesc,
  parseAcademicYearCode,
} from '../calendar/calendarYearUtils';
import {
  CalendarMonthGrid,
  boundDayEventsFromCalendar,
  TYPE_BADGE_CLASSNAMES,
  TYPE_LABELS,
} from '../calendar/CalendarMonthGrid';
import type { LEAResponse } from '../../types/api';

interface CalendarTabProps {
  lea: LEAResponse;
  canEdit: boolean;
}

const NO_CALENDAR_STATUS = 404;

function formatIsoDate(iso: string | null): string {
  if (!iso) return '—';
  // Display as a friendly date without TZ shifts
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export const CalendarTab: React.FC<CalendarTabProps> = ({ lea, canEdit }) => {
  const { getBool } = useAppSettings();
  const editingEnabled = getBool(APP_SETTING_KEYS.calendarEditingEnabled, false);
  const showEditButton = canEdit && editingEnabled;

  const [academicYears, setAcademicYears] = useState<Descriptor[]>([]);
  const [specialDayTypes, setSpecialDayTypes] = useState<Descriptor[]>([]);
  const [selectedYearCode, setSelectedYearCode] = useState<string>('');
  const [calendar, setCalendar] = useState<CalendarResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Initial descriptor load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [years, types] = await Promise.all([
          descriptorsApi.getByTypeName('Academic Year'),
          descriptorsApi.getByTypeName('Special Day Type'),
        ]);
        if (cancelled) return;
        const sortedYears = [...years]
          .filter((y) => y.active)
          .sort((a, b) => compareYearCodesDesc(a.codeValue, b.codeValue));
        setAcademicYears(sortedYears);
        setSpecialDayTypes(
          [...types]
            .filter((t) => t.active)
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        );
        if (sortedYears.length > 0 && !selectedYearCode) {
          setSelectedYearCode(sortedYears[0].codeValue);
        }
      } catch (err) {
        if (!cancelled) setError(getUserErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // selectedYearCode intentionally excluded — only seeding the default
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the calendar whenever the selected year changes
  const loadCalendar = useCallback(
    async (yearCode: string) => {
      if (!yearCode) return;
      setCalendarLoading(true);
      setError(null);
      try {
        const result = await calendarApi.get(lea.educationOrganizationId, yearCode);
        setCalendar(result);
      } catch (err: any) {
        if (err?.status === NO_CALENDAR_STATUS) {
          setCalendar(null);
        } else {
          setError(getUserErrorMessage(err));
        }
      } finally {
        setCalendarLoading(false);
      }
    },
    [lea.educationOrganizationId]
  );

  useEffect(() => {
    if (selectedYearCode) loadCalendar(selectedYearCode);
  }, [selectedYearCode, loadCalendar]);

  const handleSave = useCallback(
    async (payload: CalendarUpsert) => {
      const updated = await calendarApi.upsert(
        lea.educationOrganizationId,
        selectedYearCode,
        payload
      );
      setCalendar(updated);
      setSuccessMessage(`Calendar saved for ${selectedYearCode}.`);
      // Clear the success banner after a few seconds
      window.setTimeout(() => setSuccessMessage(null), 4000);
    },
    [lea.educationOrganizationId, selectedYearCode]
  );

  const chronologicalDays = useMemo(() => {
    if (!calendar) return [] as Array<{ date: string; typeCode: string }>;
    return [...calendar.days]
      .map((d) => ({
        date: d.calendarDate,
        typeCode: d.specialDayTypeCode ?? `Type #${d.specialDayTypeDescriptorId}`,
      }))
      .sort((a, b) =>
        a.date === b.date ? a.typeCode.localeCompare(b.typeCode) : a.date.localeCompare(b.date)
      );
  }, [calendar]);

  // The four scalar bound-dates (student/teacher first/last day), surfaced as
  // synthetic markers so they appear in the grid + list like special days.
  const boundDayEvents = useMemo(
    () => (calendar ? boundDayEventsFromCalendar(calendar) : []),
    [calendar]
  );

  // Special days + bound-date markers, what the grid and chronological list
  // both render.
  const allMarkers = useMemo(
    () => [...chronologicalDays, ...boundDayEvents],
    [chronologicalDays, boundDayEvents]
  );

  // Consolidate same-date entries: one row per date with the list of types
  // for that date. Then group by month for the headers.
  const daysByMonth = useMemo(() => {
    const byDate = new Map<string, string[]>();
    for (const ev of allMarkers) {
      const list = byDate.get(ev.date) ?? [];
      list.push(ev.typeCode);
      byDate.set(ev.date, list);
    }
    const consolidated = Array.from(byDate.entries())
      .map(([date, types]) => ({ date, types }))
      .sort((a, b) => a.date.localeCompare(b.date));

    const groups = new Map<string, typeof consolidated>();
    for (const entry of consolidated) {
      const key = entry.date.slice(0, 7);
      const list = groups.get(key) ?? [];
      list.push(entry);
      groups.set(key, list);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [allMarkers]);

  const yearBounds = useMemo(
    () => parseAcademicYearCode(selectedYearCode),
    [selectedYearCode]
  );

  if (loading) {
    return <div className="p-4 text-gray-500 dark:text-gray-400">Loading calendar metadata…</div>;
  }

  if (academicYears.length === 0) {
    return (
      <Alert color="info">
        No academic years are available yet. A super admin can add one via the
        Descriptor Management page (descriptor type "Academic Year").
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <Alert color="failure" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert color="success" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Header: year selector + edit button */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <label
            htmlFor="academicYearSelect"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Academic year
          </label>
          <Select
            id="academicYearSelect"
            value={selectedYearCode}
            onChange={(e) => setSelectedYearCode(e.target.value)}
            disabled={calendarLoading}
            className="min-w-[8rem]"
          >
            {academicYears.map((y) => (
              <option key={y.descriptorId} value={y.codeValue}>
                {y.codeValue}
              </option>
            ))}
          </Select>
        </div>

        {showEditButton && (
          <Button onClick={() => setEditModalOpen(true)} disabled={calendarLoading}>
            {calendar ? (
              <>
                <HiPencil className="mr-2 h-4 w-4" />
                Edit Calendar
              </>
            ) : (
              <>
                <HiPlus className="mr-2 h-4 w-4" />
                Create Calendar
              </>
            )}
          </Button>
        )}
      </div>

      {calendarLoading ? (
        <div className="p-4 text-gray-500 dark:text-gray-400">Loading calendar…</div>
      ) : !calendar ? (
        <Card>
          <div className="flex flex-col items-center text-center py-8">
            <HiCalendar className="h-10 w-10 text-gray-300 mb-3" />
            <p className="text-gray-700 dark:text-gray-300 font-medium">
              No calendar saved for {selectedYearCode}.
            </p>
            {showEditButton ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Click <em>Create Calendar</em> above to add one.
              </p>
            ) : canEdit && !editingEnabled ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Calendar editing is not yet enabled site-wide.
              </p>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                The district has not published a calendar for this year.
              </p>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Student days
            </h4>
            <dl className="text-sm divide-y divide-gray-100 dark:divide-gray-700">
              <div className="flex justify-between py-1">
                <dt className="text-gray-500 dark:text-gray-400">First day</dt>
                <dd className="font-medium">{formatIsoDate(calendar.studentStartDate)}</dd>
              </div>
              <div className="flex justify-between py-1">
                <dt className="text-gray-500 dark:text-gray-400">Last day</dt>
                <dd className="font-medium">{formatIsoDate(calendar.studentEndDate)}</dd>
              </div>
              <div className="flex justify-between py-1">
                <dt className="text-gray-500 dark:text-gray-400">Total student days</dt>
                <dd className="font-medium">{calendar.studentTotalDays ?? '—'}</dd>
              </div>
            </dl>
          </Card>

          <Card>
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Teacher days
            </h4>
            <dl className="text-sm divide-y divide-gray-100 dark:divide-gray-700">
              <div className="flex justify-between py-1">
                <dt className="text-gray-500 dark:text-gray-400">First day</dt>
                <dd className="font-medium">{formatIsoDate(calendar.teacherStartDate)}</dd>
              </div>
              <div className="flex justify-between py-1">
                <dt className="text-gray-500 dark:text-gray-400">Last day</dt>
                <dd className="font-medium">{formatIsoDate(calendar.teacherEndDate)}</dd>
              </div>
              <div className="flex justify-between py-1">
                <dt className="text-gray-500 dark:text-gray-400">Total teacher days</dt>
                <dd className="font-medium">{calendar.teacherTotalDays ?? '—'}</dd>
              </div>
            </dl>
          </Card>

          <div className="lg:col-span-2">
            <Card>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Special days &amp; key dates{' '}
                <span className="font-normal text-gray-500 dark:text-gray-400 text-sm">
                  ({allMarkers.length})
                </span>
              </h4>
              {allMarkers.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No special days recorded.</p>
              ) : (
                <div className="space-y-6">
                  {/* Big month-grid visual — full-width, one month at a time */}
                  <CalendarMonthGrid
                    events={allMarkers}
                    fromDate={yearBounds?.fromDate}
                    toDate={yearBounds?.toDate}
                  />

                  {/* Compact chronological list, grouped by month, with
                      multi-type days consolidated onto a single row. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
                    {daysByMonth.map(([monthKey, items]) => (
                      <div key={monthKey}>
                        <h5 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                          {(() => {
                            const [y, m] = monthKey.split('-').map(Number);
                            return new Date(y, m - 1, 1).toLocaleDateString(undefined, {
                              month: 'long',
                              year: 'numeric',
                            });
                          })()}
                        </h5>
                        <ul className="text-sm divide-y divide-gray-100 dark:divide-gray-800">
                          {items.map(({ date, types }) => (
                            <li
                              key={date}
                              className="py-1.5 flex items-baseline justify-between gap-3"
                            >
                              <span className="font-mono text-xs text-gray-700 dark:text-gray-300 shrink-0">
                                {(() => {
                                  const [y, m, d] = date.split('-').map(Number);
                                  return new Date(y, m - 1, d).toLocaleDateString(
                                    undefined,
                                    {
                                      weekday: 'short',
                                      month: 'short',
                                      day: 'numeric',
                                    }
                                  );
                                })()}
                              </span>
                              <span className="flex flex-wrap justify-end gap-1">
                                {types.map((typeCode) => {
                                  const badgeClass =
                                    TYPE_BADGE_CLASSNAMES[typeCode] ??
                                    'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
                                  const label = TYPE_LABELS[typeCode] ?? typeCode;
                                  return (
                                    <span
                                      key={`${date}-${typeCode}`}
                                      className={`text-xs px-2 py-0.5 rounded ${badgeClass}`}
                                    >
                                      {label}
                                    </span>
                                  );
                                })}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}

      <EditCalendarModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleSave}
        calendar={calendar}
        academicYearCode={selectedYearCode}
        specialDayTypes={specialDayTypes}
        organizationName={lea.nameOfInstitution}
      />
    </div>
  );
};
